import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useFormat } from "@/contexts/FormatContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, ArrowLeft, Users, Zap, X, Siren, LineChart, Scissors, Flame, Telescope } from "lucide-react";
import RookieScoutPanel from "@/components/RookieScoutPanel";
import FormatBadge from "@/components/FormatBadge";
import HeatMapPanel from "@/components/HeatMapPanel";
import ManagersTab from "./leaguecontext/ManagersTab";
import DesperationTab from "./leaguecontext/DesperationTab";
import PickSimTab from "./leaguecontext/PickSimTab";
import CrunchTab from "./leaguecontext/CrunchTab";

const TABS = [
  { id: "managers", label: "Manager Personas", icon: Users },
  { id: "desperation", label: "Desperation Index", icon: Siren },
  { id: "picksim", label: "Pick Futures", icon: LineChart },
  { id: "crunch", label: "Roster Crunch", icon: Scissors },
  { id: "rookies", label: "Rookie Scouting", icon: Telescope },
  { id: "heatmap", label: "Trade Heat Map", icon: Flame },
];

export default function LeagueContext() {
  const { id: routeId } = useParams();
  const standalone = !routeId;
  const { format, setResolvedFormat } = useFormat();
  const [pickerLeagues, setPickerLeagues] = useState(null);
  const [pickerId, setPickerId] = useState("");
  const id = routeId || pickerId;
  const [tab, setTab] = useState("managers");
  const [ctx, setCtx] = useState(null);
  const [managers, setManagers] = useState(null);
  const [desperation, setDesperation] = useState(null);
  const [pickProj, setPickProj] = useState(null);
  const [crunch, setCrunch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pitch, setPitch] = useState(null); // {manager, content, loading}

  const draftPitch = async (m) => {
    setPitch({ manager: m.manager, content: "", loading: true });
    try {
      const r = await api.post(`/leagues/${id}/managers/${m.roster_id}/pitch`, {});
      setPitch({ manager: m.manager, content: r.data.pitch, loading: false });
    } catch (e) {
      setPitch({ manager: m.manager, content: "⚠️ " + (e?.response?.data?.detail || "Could not draft a pitch right now."), loading: false });
    }
  };

  useEffect(() => {
    if (!standalone) return;
    api.get("/leagues").then(r => {
      const ls = r.data.leagues || [];
      setPickerLeagues(ls);
      setPickerId(prev => prev || ls[0]?.league_id || "");
    });
  }, [standalone]);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    setLoading(true);
    setManagers(null); setDesperation(null); setPickProj(null); setCrunch(null);
    api.get(`/leagues/${id}/context`).then(r => {
      setCtx(r.data);
      setResolvedFormat(r.data?.league?.format || null);
    }).finally(() => setLoading(false));
  }, [id, format]); // eslint-disable-line
  useEffect(() => () => setResolvedFormat(null), [setResolvedFormat]);

  useEffect(() => {
    if (!id) return;
    if (tab === "managers" && managers === null) api.get(`/leagues/${id}/managers`).then(r => setManagers(r.data));
    if (tab === "desperation" && desperation === null) api.get(`/leagues/${id}/desperation`).then(r => setDesperation(r.data));
    if (tab === "picksim" && pickProj === null) api.get(`/leagues/${id}/pick-projection`).then(r => setPickProj(r.data));
    if (tab === "crunch" && crunch === null) api.get(`/leagues/${id}/roster-crunch`).then(r => setCrunch(r.data));
  }, [tab, id]); // eslint-disable-line

  const headerEl = (
    <div>
      {standalone ? (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2 text-[var(--accent)] text-xs tracking-widest font-mono">
            <Zap className="w-3.5 h-3.5" /> // DME LEAGUE INTELLIGENCE
          </div>
          {pickerLeagues && pickerLeagues.length > 0 && (
            <select className="input-dark md:w-72" value={pickerId} onChange={e => setPickerId(e.target.value)} data-testid="intel-league-select">
              {pickerLeagues.map(l => <option key={l.league_id} value={l.league_id}>{l.name}</option>)}
            </select>
          )}
        </div>
      ) : (
        <>
          <Link to="/app" className="text-xs text-[var(--text-3)] hover:text-[var(--accent)] inline-flex items-center gap-1 mb-3"><ArrowLeft className="w-3 h-3" /> Dashboard</Link>
          <div className="flex items-center gap-2 text-[var(--accent)] text-xs tracking-widest mb-1 font-mono">
            <Zap className="w-3.5 h-3.5" /> // DME LEAGUE INTELLIGENCE
          </div>
        </>
      )}
      <h1 className="font-display text-4xl font-black uppercase tracking-tight inline-flex items-center gap-3" data-testid="ctx-heading">{standalone ? "DME League Intelligence" : (ctx?.league?.name || "League Context")} {ctx && <FormatBadge format={ctx.league?.league_format || ctx.league?.format} />}</h1>
      <p className="text-[var(--text-2)] mt-2 max-w-2xl">
        Deep intelligence on your league — manager tendencies, roster pressure, pick futures, and the trade targets that move you up.
        {ctx && <span className="text-[var(--text-3)] ml-1 font-mono">{ctx.lineup?.summary} · {ctx.lineup?.teams} teams</span>}
      </p>
    </div>
  );

  if (loading) return <div className="space-y-6 fade-up">{standalone && headerEl}<div className="card-bordered p-12 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div></div>;
  if (standalone && pickerLeagues && pickerLeagues.length === 0) return <div className="space-y-6 fade-up">{headerEl}<div className="card-bordered p-8 text-center text-[var(--text-2)]" data-testid="intel-empty">Sync a Sleeper league from your Dashboard to unlock DME League Intelligence.</div></div>;
  if (!ctx) return <div className="space-y-6 fade-up">{standalone && headerEl}<div className="card-bordered p-8 text-center text-[var(--text-2)]">Could not load league context.</div></div>;

  return (
    <div className="space-y-6 fade-up">
      {headerEl}

      <div className="flex gap-2 border-b border-[var(--border)] pb-3 flex-wrap">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ctx-tab-${t.id}`}
            className={`text-sm px-3 py-1.5 rounded-sm inline-flex items-center gap-2 transition-colors ${tab === t.id ? "bg-[var(--accent)] text-black font-semibold" : "text-[var(--text-2)] hover:text-white border border-[var(--border)]"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "managers" && <ManagersTab managers={managers} draftPitch={draftPitch} />}
      {tab === "desperation" && <DesperationTab desperation={desperation} draftPitch={draftPitch} />}
      {tab === "picksim" && <PickSimTab pickProj={pickProj} />}
      {tab === "crunch" && <CrunchTab crunch={crunch} />}
      {tab === "rookies" && <RookieScoutPanel id={id} />}
      {tab === "heatmap" && <HeatMapPanel id={id} />}

      {pitch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="pitch-modal" onClick={() => setPitch(null)}>
          <div className="card-bordered w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPitch(null)} className="absolute top-4 right-4 text-[var(--text-3)] hover:text-white" data-testid="pitch-close"><X className="w-5 h-5" /></button>
            <div className="text-[var(--accent)] text-xs tracking-widest mb-1 font-mono">// AI TRADE PITCH</div>
            <div className="font-display text-2xl font-black uppercase tracking-tight mb-4">Target: {pitch.manager}</div>
            {pitch.loading ? (
              <div className="py-10 flex flex-col items-center gap-3 text-[var(--text-3)]">
                <Loader2 className="w-6 h-6 spin text-[var(--accent)]" />
                <span className="text-sm">Drafting a pitch tuned to their tendencies…</span>
              </div>
            ) : (
              <div className="md-body text-sm"><ReactMarkdown remarkPlugins={[remarkGfm]}>{pitch.content}</ReactMarkdown></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
